import React from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Components, Theme, Utils } from 'versafleet-core';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import Footer from 'modules/footer';
import VisualState from './visualState';

const { Color } = Theme;
const { Helper, RequestHelper, RouteHelper } = Utils;

const MAX_ZOOM = 18;
const MIN_ZOOM = 0;

function WithRouterHooks(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Handle navigation in useEffect to avoid React warnings
  React.useEffect(() => {
    if (!Helper.isNullOrUndefined(location.state)) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  return <TrackingDetail {...props} location={location} navigate={navigate} params={params} />;
}

class TrackingDetail extends React.Component {
  constructor(props) {
    super(props);
    const { state } = props.location;
    this.state = {
      data: state && state.data,
      epodValue: '',
      epodError: false,
      logoUrl: state && state.logoUrl,
      markers: state && this._getMarkers(props.location.state.data.task),
      mapView: {
        lat: 1.3521,
        lng: 103.8198,
        zoom: 10,
        mapBounds: {},
      },
      eta: {},
      showEpodModal: false,
      isContactlessDeliveryEnabled: false,
      isContactlessDeliveryButtonLoading: false,
    };
    this.timer = null;
    this.driverLocationTimer = null;
  }

  componentDidMount() {
    const { location } = this.props;
    if (Helper.isNullOrUndefined(location.state)) {
      this._getLogo();
      this._getTrackingStatus();
      this.timer = setInterval(this._getTrackingStatus, 60000);
    } else {
      // Clear the location state by navigating to the same path without state
      // This is done in useEffect in the wrapper component to avoid React warnings
      this._getTrackingStatus();
      this.timer = setInterval(this._getTrackingStatus, 60000);
      this._setBounds();
    }
    this.driverLocationTimer = setInterval(this._getDriverLocation, 10000);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
    clearInterval(this.driverLocationTimer);
  }

  _trackingNumber() {
    return this.props.params.detail;
  }

  _domain() {
    const domain = this.props.params.domain;
    return domain && domain !== 'all' ? domain : null;
  }

  _getLogo = async () => {
    const data = await RequestHelper.request(`/accounts/${this._domain()}/get_logo`, { method: 'GET' });
    this.setState({ logoUrl: data.logo_url });
  }

  _getTrackingStatus = async () => {
    const url = this._isDomainSpecific()
      ? `/accounts/${this._domain()}/tasks/${this._trackingNumber()}/track`
      : `/tasks/${this._trackingNumber()}/track`;

    try {
      const data = await RequestHelper.request(url, { method: 'POST' });
      const markers = this._getMarkers(data.task);

      const positions = {
        driverPosition: markers.driverPosition,
        taskPosition: markers.taskPosition,
      };

      const eta = await this._getETA(positions);

      this.setState({
        data,
        eta,
        markers,
        isContactlessDeliveryEnabled: data?.task?.is_contactless,
      });
    } catch (error) {
      const pathname = Helper.isNullOrUndefined(this._domain())
        ? '/' : `/${this._domain()}`;
      const { navigate } = this.props;

      navigate(pathname, {
        state: {
          error,
          trackingNumber: this._trackingNumber(),
        },
      });
    }
  }

  _getDriverLocation = async () => {
    const { data } = this.state;
    if (!data || !this._shouldDisplayMap(data.task)) {
      return;
    }

    // The map is only displayed if it is domain specific.
    const url = `/accounts/${this._domain()}/tasks/${this._trackingNumber()}/driver_location`;

    try {
      const result = await RequestHelper.request(url, { method: 'GET' });
      if (result && result.latitude && result.longitude) {
        const { markers } = this.state;
        this.setState({
          markers: {
            ...markers,
            driverPosition: {
              lat: result.latitude,
              lng: result.longitude,
            },
          },
        });
      }
    } catch {
      // Silently ignore — the main timer handles fatal errors
    }
  }

  _enableContactlessDelivery = async () => {
    const { task } = this.state.data;

    this.setState({ isContactlessDeliveryButtonLoading: true }, async () => {
      try {
        await RequestHelper.request(`/tasks/${task.id}/enable_contactless_delivery`, { method: 'PUT' });
        this.setState({
          isContactlessDeliveryButtonLoading: false,
          isContactlessDeliveryEnabled: true,
        });
      } catch (e) {
        this.setState({
          isContactlessDeliveryButtonLoading: false,
        });
      }
    });
  }

  _onMapCenterChange = (center) => {
    const { mapView } = this.state;
    this.setState({
      mapView: {
        ...mapView,
        lat: center.lat(),
        lng: center.lng(),
      },
    });
  }

  _isDomainSpecific() {
    return !Helper.isNullOrUndefined(this._domain());
  }

  _updateMapZoom = (zoom) => {
    const { mapView } = this.state;
    this.setState({
      mapView: {
        ...mapView,
        zoom,
      },
    });
  }

  _centerDriver(driverPosition) {
    const { mapView } = this.state;
    this.setState({
      mapView: {
        ...mapView,
        lat: driverPosition.lat,
        lng: driverPosition.lng,
      },
    });
  }

  _setBounds() {
    const { data, markers, mapView } = this.state;
    if (!Helper.isNullOrUndefined(data)) {
      const { task } = data;

      if (this._shouldDisplayMap(task)) {
        if (!google?.maps?.LatLngBounds) {
          console.error('Google Maps API not available');
          return;
        }
        const latLngBounds = new google.maps.LatLngBounds();
        latLngBounds.extend(markers.driverPosition);
        latLngBounds.extend(markers.baseTaskPosition);
        latLngBounds.extend(markers.taskPosition);

        const mapBounds = {
          southwest: {
            lat: latLngBounds.getSouthWest().lat(),
            lng: latLngBounds.getNorthEast().lng(),
          },
          northeast: {
            lat: latLngBounds.getNorthEast().lat(),
            lng: latLngBounds.getNorthEast().lng(),
          },
        };
        this.setState({
          mapView: {
            ...mapView,
            mapBounds,
          },
        });
      }
    }
  }

  _getMarkers(task) {
    if (this._shouldDisplayMap(task)) {
      const driverLocation = task.task_assignment.driver.latest_location;
      const driverPosition = driverLocation
        && driverLocation.latitude && driverLocation.longitude ? {
          lat: driverLocation.latitude,
          lng: driverLocation.longitude,
        } : null;

      const baseTaskAddress = task.base_task.address;
      const baseTaskPosition = baseTaskAddress.latitude && baseTaskAddress.longitude ? {
        lat: baseTaskAddress.latitude,
        lng: baseTaskAddress.longitude,
      } : null;

      const taskPosition = task.address.latitude && task.address.longitude ? {
        lat: task.address.latitude,
        lng: task.address.longitude,
      } : null;

      return {
        driverPosition,
        baseTaskPosition,
        taskPosition,
      };
    }

    return {
      driverPosition: null,
      baseTaskPosition: null,
      taskPosition: null,
    };
  }

  _getMarkerUrl = (options) => {
    const svg = (
      <Components.Svg>
        <Components.Svg.Use
          fill={options.fillColor}
          href="versa-marker-plain"
          stroke={options.strokeColor}
          strokeWidth={options.isBold ? 1.6 : 0.98}
        />
        {
          options.isHalfMarker ? (
            <Components.Svg.Use
              fill={options.halfMarkerColor}
              href="versa-marker-half"
              stroke={options.strokeColor}
              strokeWidth={options.isBold ? 1.6 : 0.98}
            />
          ) : false
        }
        <Components.Svg.Use
          fill={options.iconColor}
          height={17}
          href={options.arrowPath}
          y={5}
        />
      </Components.Svg>
    );

    const uri = `data:image/svg+xml;utf-8, ${ReactDOMServer.renderToStaticMarkup(svg)}`;

    return uri.split('#').join('%23');
  }

  _getDriverMarkerUrl() {
    return this._getMarkerUrl({
      fillColor: Color.$blue,
      iconColor: Color.$white,
      arrowPath: 'versa-driver',
    });
  }

  _getBaseTaskMarkerUrl() {
    return this._getMarkerUrl({
      fillColor: Color.$blue,
      iconColor: Color.$white,
      arrowPath: 'versa-warehouse',
    });
  }

  _getTaskMarkerUrl(isDeliveryJob) {
    return this._getMarkerUrl({
      fillColor: Color.$blue,
      iconColor: Color.$white,
      arrowPath: isDeliveryJob ? 'versa-arrow-down' : 'versa-arrow-up',
    });
  }

  _shouldDisplayMap(task) {
    if (!this._isDomainSpecific()) {
      return false;
    }

    if (task.state !== 'started') {
      return false;
    }

    if (!task.task_assignment.driver.latest_location) {
      return false;
    }

    const timeStarted = moment(task.last_started_at);
    const driverUpdated = moment(task.task_assignment.driver.latest_location.created_at);
    if (driverUpdated.isBefore(timeStarted)) {
      return false;
    }

    return true;
  }

  _doDirectionService = (positions) => {
    if (!google?.maps?.DirectionsService) {
      return Promise.reject(new Error('Google Maps API not available'));
    }
    const directionsService = new google.maps.DirectionsService();
    const request = {
      origin: { lat: positions.driverPosition.lat, lng: positions.driverPosition.lng },
      destination: { lat: positions.taskPosition.lat, lng: positions.taskPosition.lng },
      travelMode: 'DRIVING',
    };
    return new Promise((resolve, reject) => {
      directionsService.route(request, (result, status) => {
        if (status === 'OK') {
          resolve(result);
        } else {
          reject(status);
        }
      });
    });
  }

  _getETA = async (positions) => {
    const today = new Date();
    let duration = '-';

    try {
      const durationInSeconds = await RouteHelper.getDuration(
        positions.driverPosition, positions.taskPosition,
      );

      if (!Helper.isNullOrUndefined(durationInSeconds)) {
        duration = `${Math.round(durationInSeconds / 60)} Mins`;
      }

      return {
        duration,
        updatedTime: moment(today).format('DD MMM YYYY, h:mm A'),
      };
    } catch (error) {
      return {
        duration,
        updatedTime: moment(today).format('DD MMM YYYY, h:mm A'),
      };
    }
  }

  _requestEpod = async () => {
    this.setState({ isLoading: true });
    const { task } = this.state.data;
    const taskType = (task.state === 'successful' && task.job.job_type === 'pickup')
      || (task.state === 'collected' && task.job.job_type === 'delivery') ? 'base' : 'task';

    try {
      const result = await RequestHelper.request(
        `/tasks/${this._trackingNumber()}/verify_epod_custom_field`,
        { method: 'POST' },
        { value: this.state.epodValue, type: taskType },
      );
      if (result.epod_url) {
        window.open(result.epod_url, '_blank');
        this.setState({ showEpodModal: false, isLoading: false, epodValue: '' });
      } else {
        // to prevent the page from stucking at the modal if there is no epod_url
        this.setState({ epodError: true, isLoading: false });
      }
    } catch {
      this.setState({ epodError: true, isLoading: false });
    }
  }

  _shouldDisplayTaskAssignmentDetails(taskState) {
    if (!this._isDomainSpecific()) {
      return false;
    }

    if (taskState === 'Started') {
      return true;
    }

    return false;
  }

  _shouldDisplayETA = (eta, task) => !Helper.isNullOrUndefined(eta)
    && !Helper.isNullOrUndefined(eta.duration) && task.state === 'started'

  _renderEpodModal = () => {
    const { task } = this.state.data;
    const epoc = task.state === 'collected';

    return (
      <Components.Modal
        className="vf-tracking__detail-container__details__epod-modal"
        visible={this.state.showEpodModal}
      >
        <div className="vf-tracking__detail-container__details__epod-modal__title">
          {`View ${epoc ? 'eProof of Collection' : 'eProof of Delivery'}`}
        </div>
        <div className="vf-tracking__detail-container__details__epod-modal__fieldname">
          {this.state.data.task.account.tracking_setting.field_description}
        </div>
        <Components.Input.Textbox
          onChange={value => this.setState({ epodValue: value })}
          placeholder={`Enter ${this.state.data.task.account.tracking_setting.field_description || ''}`}
          value={this.state.epodValue}
          width="full"
        />
        {
          this.state.epodError ? (
            <div className="vf-tracking__detail-container__details__epod-modal__error">
              Incorrect.
            </div>
          ) : false
        }
        <div className="vf-tracking__detail-container__details__epod-modal__buttons__container">
          <Components.Button
            className="vf-tracking__detail-container__details__epod-modal__buttons__button
              vf-tracking__detail-container__details__epod-modal__buttons__button__left"
            onClick={() => { this.setState({ showEpodModal: false, epodError: false, epodValue: '' }); }}
          >
            Cancel
          </Components.Button>
          {Helper.isNonEmptyString(this.state.epodValue) && (
            <Components.Button
              className="vf-tracking__detail-container__details__epod-modal__buttons__button"
              loading={this.state.isLoading}
              onClick={() => { this._requestEpod(); }}
            >
              OK
            </Components.Button>
          )}
        </div>
      </Components.Modal>
    );
  }

  _renderInfo() {
    const { data } = this.state;
    const { task } = data;
    const trackingSetting = (task.account || {}).tracking_setting || {};
    const updatedAt = task.updated_at;
    const jobType = task.job.job_type;

    const viewEPOD = task.state === 'successful' && trackingSetting.epod_attachment_enabled;
    const viewEPOC = task.state === 'collected' && trackingSetting.epod_attachment_enabled;

    const incompleteReason = task?.incomplete_reason;

    const isRescheduled = task.rescheduled_count > 0 && trackingSetting.enable_task_rescheduling;

    function formatTimeWindow(timeFrom, timeTo) {
      if (!timeFrom || !timeTo) {
        return '';
      }

      // Format the date and time
      const date = moment(timeFrom).format('D MMMM YYYY');
      const fromTime = moment(timeFrom).format('H:mm');
      const toTime = moment(timeTo).format('H:mm');

      return `${date}, ${fromTime} to ${toTime}`;
    }

    let taskState;
    let description;
    let iconClassName;
    let iconColor = Color.$greendarker;
    switch (task.state) {
      case 'created':
      case 'in_progress':
      case 'unassigned':
      case 'assigned':
      case 'waiting_for_acknowledgement':
      case 'declined':
        taskState = 'Processing';
        description = 'Your order is being processed at the moment.';
        iconClassName = 'versa-waiting2';
        break;
      case 'acknowledged':
        taskState = 'Assigned';
        description = 'A driver has been assigned to your order.';
        iconClassName = 'versa-warehouse';
        break;
      case 'collected':
        taskState = 'Collected';
        description = 'Our driver has collected your order.';
        iconClassName = 'versa-arrow-up';
        break;
      case 'started':
        taskState = 'Started';
        description = `Our driver is on the way to ${jobType === 'delivery' ? 'deliver' : 'collect'} your order.`;
        iconClassName = 'versa-otw';
        break;
      case 'arrived':
        taskState = 'Arrived';
        description = `Our driver has arrived to ${jobType === 'delivery' ? 'deliver' : 'collect'} your order.`;
        iconClassName = 'versa-waiting2';
        break;
      case 'successful':
        taskState = 'Delivered';
        description = 'Our driver has delivered your order.';
        iconClassName = 'versa-completed';
        break;
      case 'failed':
      case 'cancelled':
        taskState = 'Incomplete';
        description = `Please contact ${task.account.name} customer service for further assistance.`;
        iconClassName = 'versa-close';
        iconColor = Color.$gold;
        break;
      default:
    }

    return (
      <div className="vf-tracking__detail-container__details__info">
        <div className="vf-tracking__detail-container__details__info__updated">
          {moment(updatedAt).format('DD MMM YYYY, h:mm A')}
        </div>
        <div className="vf-tracking__detail-container__details__info__details">
          <Components.CircleIcon
            className="vf-tracking__detail-container__details__info__details__icon"
            iconClassName={iconClassName}
            innerIconColor={Color.$white}
            outerIconColor={iconColor}
          />
          <div className="vf-tracking__detail-container__details__info__details__text">
            <div className="vf-tracking__detail-container__details__info__details__text__state">
              {taskState}
            </div>
            <div className="vf-tracking__detail-container__details__info__details__text__description">
              {incompleteReason && (
                <div className="vf-tracking__detail-container__details__info__details__text__description__reason">
                  Reason:
                  {' '}
                  {incompleteReason}
                </div>
              )}

              {isRescheduled ? (
                <React.Fragment>
                  <Components.Window.Description>
                    Your order has been rescheduled to:
                    <br />
                  </Components.Window.Description>
                  <Components.Window.Content>
                    {formatTimeWindow(task.time_from, task.time_to)}
                  </Components.Window.Content>

                  <Components.Window.Section>
                    <Components.Link
                      onClick={() => this.props.navigate('verify', { state: { fromDetailPage: true } })}
                    >
                      Request a new date & time
                    </Components.Link>
                  </Components.Window.Section>
                </React.Fragment>
              ) : (
                <Components.Window.Content>
                  {description}
                </Components.Window.Content>
              )}
            </div>
            {
              (viewEPOD || viewEPOC) && (
                <div
                  className="vf-tracking__detail-container__details__info__details__text__view-epod"
                >
                  <Components.Link
                    // onClick={() => window.open(epodUrl, '_blank')}
                    onClick={() => { this.setState({ showEpodModal: true }); }}
                  >
                    {viewEPOD && 'View eProof of Delivery'}
                    {viewEPOC && 'View eProof of Collection'}
                  </Components.Link>
                </div>
              )
            }
            {
              this._shouldDisplayTaskAssignmentDetails(taskState)
                ? this._renderTaskAssignmentDetails() : false
            }
          </div>
        </div>
      </div>
    );
  }

  _renderTaskAssignmentDetails() {
    const { eta, data } = this.state;
    const { vehicle_plate_number: vehicleNumber } = data.task.task_assignment;
    const { name } = data.task.task_assignment.driver || {};
    const etaBlock = this._shouldDisplayETA(eta, data.task)
      ? (
        <div>
          <div className="vf-tracking__detail-container__details__info__details__text__eta">
            {`Live ETA: ${this.state.eta.duration}`}
          </div>
          <div className="vf-tracking__detail-container__details__info__updated">
            (Updated at
            {!Helper.isNullOrUndefined(this.state.eta.updatedTime)
              ? ` ${this.state.eta.updatedTime}`
              : ''}
            )
          </div>
        </div>
      )
      : false;

    return (
      <React.Fragment>
        <div className="vf-tracking__detail-container__details__info__details__text__task-assignment">
          {!Helper.isNullOrUndefined(name) && (
            <div>
              <i className="versa-driver" />
              {name}
            </div>
          )}
          {!Helper.isNullOrUndefined(vehicleNumber) && (
            <div>
              <i className="versa-vehicle" />
              {vehicleNumber}
            </div>
          )}
          {etaBlock}
        </div>
      </React.Fragment>
    );
  }

  _renderLogo() {
    const { logoUrl } = this.state;
    return (
      <img
        alt="Logo"
        className="vf-tracking__detail-container__header__logo"
        src={logoUrl}
      />
    );
  }

  _renderTrackingNumberInfo() {
    return (
      <div className="vf-tracking__detail-container__header__tracking-number-info">
        <div className="vf-tracking__detail-container__header__tracking-number-info__title">
          Track your order
        </div>
        <div className="vf-tracking__detail-container__header__tracking-number-info__number">
          { this._trackingNumber() }
        </div>
      </div>
    );
  }

  _renderIcons() {
    const { data } = this.state;
    return (
      <div className="vf-tracking__detail-container__details__visual-state-container">
        <VisualState
          jobType={data.task.job.job_type}
          taskState={data.task.state}
        />
      </div>
    );
  }

  _renderHeader() {
    const { logoUrl } = this.state;

    return (
      <div className="vf-tracking__detail-container__header">
        {this._renderTrackingNumberInfo()}
        {Helper.isNullOrUndefined(logoUrl) || logoUrl === '' ? false : this._renderLogo()}
      </div>
    );
  }

  _renderDetails() {
    return (
      <div className="vf-tracking__detail-container__details">
        {this._renderInfo()}
        {this._renderIcons()}
      </div>
    );
  }

  _renderETA() {
    if (this._shouldDisplayETA(this.state.eta, this.state.data.task)) {
      return (
        <div className="vf-tracking__detail-container__map-container__eta-box">
          <div className="vf-tracking__detail-container__details__info__details__text__eta">
            {`Live ETA: ${this.state.eta.duration}`}
          </div>
          <div className="vf-tracking__detail-container__details__info__updated">
            {`Updated at ${this.state.eta.updatedTime}`}
          </div>
        </div>
      );
    }
    return false;
  }

  _renderMap() {
    const { data, mapView, markers } = this.state;

    if (!this._shouldDisplayMap(data.task)) {
      return false;
    }

    const isDeliveryJob = data.task.job.job_type === 'delivery';

    return (
      <div className="vf-tracking__detail-container__map-container">
        <Components.Map
          center={{ lat: mapView.lat, lng: mapView.lng }}
          disableZoom
          initialPositionBounds={Object.values(markers).filter(val => val)}
          mapBounds={mapView.mapBounds}
          onCenterChange={this._onMapCenterChange}
          updateZoom={this._updateMapZoom}
          zoom={mapView.zoom}
        >
          {
            markers.driverPosition ? (
              <Components.Map.Marker
                icon={this._getDriverMarkerUrl()}
                position={markers.driverPosition}
              />
            ) : false
          }

          {
            markers.baseTaskPosition ? (
              <Components.Map.Marker
                icon={this._getBaseTaskMarkerUrl(isDeliveryJob)}
                position={markers.baseTaskPosition}
              />
            ) : false
          }

          {
            markers.taskPosition ? (
              <Components.Map.Marker
                icon={this._getTaskMarkerUrl(isDeliveryJob)}
                position={markers.taskPosition}
              />
            ) : null
          }
        </Components.Map>

        <div className="vf-tracking__detail-container__map-container__map-controller">
          <Components.Map.ButtonGroup>
            <Components.Map.Button
              disabled={mapView.zoom === MAX_ZOOM}
              hint="Zoom In"
              hintPosition={Components.Hint.Position.LEFT}
              iconClassName="versa-zoom-in"
              onClick={() => this._updateMapZoom(mapView.zoom + 1)}
            />
            <Components.Map.Button
              disabled={mapView.zoom === MIN_ZOOM}
              hint="Zoom Out"
              hintPosition={Components.Hint.Position.LEFT}
              iconClassName="versa-zoom-out"
              onClick={() => this._updateMapZoom(mapView.zoom - 1)}
            />
          </Components.Map.ButtonGroup>
        </div>

        <div className="vf-tracking__detail-container__map-container__center-driver-button">
          <Components.Map.ButtonGroup>
            <Components.Map.Button
              disabled={markers.driverPosition === null}
              hint="Show Driver Location"
              hintPosition={Components.Hint.Position.LEFT}
              iconClassName="versa-driver"
              onClick={() => this._centerDriver(markers.driverPosition)}
            />
          </Components.Map.ButtonGroup>
        </div>
        {this._renderETA()}
      </div>
    );
  }

  _renderMapInfo() {
    const { data } = this.state;

    if (!this._shouldDisplayMap(data.task)) {
      return false;
    }

    const updatedAt = data.task.task_assignment.driver.latest_location.created_at;
    const isToday = (moment().format('DD MMMM YYYY') === moment(updatedAt).format('DD MMMM YYYY'));
    const updatedAtTime = moment(updatedAt).format('h:mm A');
    const updatedAtDay = isToday ? 'today' : moment(updatedAt).format('D MMM YYYY');

    return (
      <div className="vf-tracking__detail-container__map-info">
        <div className="vf-tracking__detail-container__map-info__driver-updated">
          {
            updatedAt ? `Last updated ${updatedAtDay}, ${updatedAtTime}.` : ''
          }
        </div>
        <div className="vf-tracking__detail-container__map-info__warning">
          Driver location based on mobile GPS data may be inaccurate.
        </div>
      </div>
    );
  }

  _renderContactlessDeliveryButton() {
    if (this.state.data?.task?.account?.tracking_setting?.contactless_delivery
      && this.state.data?.task?.state !== 'successful'
    ) {
      return (
        <Components.Button
          className="vf-tracking__contactless-button"
          disabled={this.state.isContactlessDeliveryEnabled}
          fillColor
          loading={this.state.isContactlessDeliveryButtonLoading}
          onClick={this._enableContactlessDelivery}
        >
          {this.state.isContactlessDeliveryEnabled ? 'Contactless Delivery selected!' : 'Click here to opt in for Contactless Delivery'}
        </Components.Button>
      );
    }
    return false;
  }

  render() {
    const { data } = this.state;
    if (Helper.isNullOrUndefined(data)) {
      return null;
    }

    return (
      <div className="vf-tracking">
        <Components.Container
          className="vf-tracking__detail-container"
        >
          {this._renderHeader()}
          {this._renderDetails()}
          {this._renderMap()}
          {this._renderMapInfo()}
        </Components.Container>

        {this._renderContactlessDeliveryButton()}

        <Footer />

        {this._renderEpodModal()}
      </div>
    );
  }
}

TrackingDetail.displayName = 'Tracking Detail';

TrackingDetail.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.shape({
      data: PropTypes.shape({
        task: PropTypes.shape({}),
      }),
      logoUrl: PropTypes.string,
    }),
  }).isRequired,
  navigate: PropTypes.func.isRequired,
  params: PropTypes.shape({
    detail: PropTypes.string,
    domain: PropTypes.string,
  }).isRequired,
};

export default WithRouterHooks;
